
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { isUnauthorizedPlatformApiError } from '@/services/platformApi/client';
import { getUserFavorites, addFavorite, removeFavorite } from '@/services/favoritesService';

interface FavoritesContextType {
  favorites: string[];
  toggleFavorite: (providerId: string) => Promise<void>;
  isFavorite: (providerId: string) => boolean;
  isLoading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }: { children: React.ReactNode }) => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, loading } = useAuth();

  const fetchFavorites = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const favoriteIds = await getUserFavorites(user.id);
      setFavorites(favoriteIds);
    } catch (error) {
      if (!isUnauthorizedPlatformApiError(error)) {
        console.error('Error fetching favorites:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && user) {
      fetchFavorites();
    } else {
      setFavorites([]);
    }
  }, [fetchFavorites, loading, user]);

  const toggleFavorite = async (providerId: string) => {
    if (!user) return;

    try {
      const isFav = favorites.includes(providerId);
      
      if (isFav) {
        // Remove from favorites
        await removeFavorite(user.id, providerId);
        setFavorites(prev => prev.filter(id => id !== providerId));
      } else {
        // Add to favorites
        await addFavorite(user.id, providerId);
        setFavorites(prev => [...prev, providerId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const isFavorite = (providerId: string) => {
    return favorites.includes(providerId);
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, isLoading }}>
      {children}
    </FavoritesContext.Provider>
  );
};
